import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SortIcon from "components/Icons/SortIcon";
import type { ButtonProps } from "@mui/material";
import TooltipHelper from "components/TooltipHelper";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import useSetTableSort from "providers/TableSortProvider/useSetTableSort";
import useTableSort from "providers/TableSortProvider/useTableSort";
import * as Components from "./TableHeaderSortButton.components";

export type SortDataType = "text" | "date" | "numeric" | "custom";
export type SortTooltipKeys = { asc: string; desc: string };

const DEFAULT_TOOLTIP_KEYS: Record<SortDataType, SortTooltipKeys | null> = {
  text: { asc: "global.sort_by_name_ascending", desc: "global.sort_by_name_descending" },
  numeric: { asc: "global.sort_by_value_lowest_first", desc: "global.sort_by_value_highest_first" },
  date: { asc: "global.sort_by_date_oldest_first", desc: "global.sort_by_date_newest_first" },
  custom: null,
};

export interface TableHeaderSortButtonProps<T> {
  propKey: keyof T;
  label: string;
  startIcon?: ButtonProps["startIcon"];
  sx?: ButtonProps["sx"];
  sortDataType?: SortDataType;
  customSortLabels?: SortTooltipKeys;
}

function TableHeaderSortButton<T extends Record<string, unknown>>(props: TableHeaderSortButtonProps<T>) {
  const { label, propKey, startIcon, sortDataType, customSortLabels, ...rest } = props;

  const { t } = useTranslation();
  const sortBy = useTableSort();
  const setSortBy = useSetTableSort();

  const isSortActive = useMemo(() => sortBy.key === propKey, [sortBy, propKey]);

  const ariaSort = useMemo(
    () => (isSortActive ? (sortBy.direction === "asc" ? "ascending" : "descending") : "none"),
    [isSortActive, sortBy.direction]
  );

  const title = useMemo(() => {
    if (!sortDataType) return null;

    if (sortDataType === "custom" && customSortLabels) {
      const customTooltipKey = isSortActive
        ? sortBy.direction === "asc"
          ? customSortLabels.asc
          : customSortLabels.desc
        : customSortLabels.asc;

      return customTooltipKey;
    }

    const keys = DEFAULT_TOOLTIP_KEYS[sortDataType];
    if (!keys) return null;

    const tooltipKey = isSortActive ? (sortBy.direction === "asc" ? keys.asc : keys.desc) : keys.asc;
    return t(tooltipKey);
  }, [sortDataType, customSortLabels, isSortActive, sortBy.direction, t]);

  const onClick = useCallback(() => {
    setSortBy((prev) => ({
      ...prev,
      key: propKey,
      direction: prev.key === propKey ? (prev.direction === "asc" ? "desc" : "asc") : "asc",
    }));
  }, [setSortBy, propKey]);

  return (
    <ThemeProvider2024>
      <TooltipHelper title={title} enterDelay={750} placement="top">
        <Components.SortButton
          aria-sort={ariaSort}
          aria-label={label}
          variant="text"
          size="small"
          color="inherit"
          onClick={onClick}
          fullWidth={false}
          data-active={isSortActive}
          startIcon={startIcon}
          endIcon={
            <SortIcon
              direction={isSortActive ? sortBy.direction : "asc"}
              data-active={isSortActive}
              sx={{
                color: "grey.60",
                "&[data-active='true']": {
                  color: "currentColor",
                },
              }}
            />
          }
          {...rest}
        >
          {label}
        </Components.SortButton>
      </TooltipHelper>
    </ThemeProvider2024>
  );
}

export default TableHeaderSortButton;
